import { useState, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import { ImageUploader } from '../common/ImageUploader';
import { Z_INDEX, getZIndexClass } from '../../utils/zIndexUtils';

interface DeliveryInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { instructions: string; images: string[] }) => void;
  currentInstructions: string;
  currentImages?: string[];
  loading?: boolean;
}

export default function DeliveryInstructionsModal({
  isOpen,
  onClose,
  onSave,
  currentInstructions,
  currentImages = [],
  loading = false
}: DeliveryInstructionsModalProps) {
  const [instructions, setInstructions] = useState(currentInstructions);
  const [images, setImages] = useState<string[]>(currentImages);
  // Add a reference to track the last valid images received from ImageUploader
  const lastValidImagesRef = useRef<string[]>([]);

  if (!isOpen) return null;

  // Enhance safeSetImages to maintain the reference of known good images
  const safeSetImages = (imageUrls: string[]) => {
    // Filter out any blob URLs to prevent errors with expired blobs
    const filteredImages = imageUrls.filter(img => !img.startsWith('blob:'));
    
    if (filteredImages.length !== imageUrls.length) {
      console.log('[DeliveryInstructionsModal] Filtered out', imageUrls.length - filteredImages.length, 'blob URLs from images');
      
      // If we're left with no images but had valid ones before, keep the last known good images
      if (filteredImages.length === 0 && lastValidImagesRef.current.length > 0) {
        console.log('[DeliveryInstructionsModal] Using last known valid images instead of empty array');
        setImages(lastValidImagesRef.current);
        return;
      }
    }
    
    // If we have valid images, update our reference
    if (filteredImages.length > 0) {
      lastValidImagesRef.current = filteredImages;
    }
    
    setImages(filteredImages);
  };
  
  // Handle image change from the ImageUploader
  const handleImagesChange = (newImages: string[]) => {
    console.log('[DeliveryInstructionsModal] Received new images from ImageUploader:', newImages);
    
    // Only process non-blob URLs
    const validImages = newImages.filter(img => !img.startsWith('blob:'));
    
    // Check if we actually have valid HTTP URLs (not blobs)
    if (validImages.length > 0) {
      console.log('[DeliveryInstructionsModal] Accepted valid images:', validImages);
      lastValidImagesRef.current = validImages;
      setImages(validImages);
    } else if (newImages.length === 0) {
      // If the uploader is clearing all images, respect that
      console.log('[DeliveryInstructionsModal] Clearing all images');
      lastValidImagesRef.current = [];
      setImages([]);
    } else {
      // Log but ignore blob URLs - don't update state
      console.log('[DeliveryInstructionsModal] Ignoring update with only blob URLs');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSave = () => {
    // Final validation of images to ensure no blob URLs are included
    const validImages = images.filter(img => !img.startsWith('blob:'));
    
    // If we've filtered out everything but had valid images before, use the last known good ones
    if (validImages.length === 0 && lastValidImagesRef.current.length > 0) {
      console.log('[DeliveryInstructionsModal] Using last known valid images instead of empty array for saving');
      validImages.push(...lastValidImagesRef.current);
    }
    
    if (validImages.length !== images.length) {
      console.log('[DeliveryInstructionsModal] Filtered out blob URLs before saving:', images.length - validImages.length);
      // Update the UI with cleaned images
      safeSetImages(validImages);
    }
    
    onSave({ instructions, images: validImages });
  };

  const hasChanges = instructions !== currentInstructions || 
    images.length !== currentImages.length || 
    images.some((img, i) => img !== currentImages[i]);

  return (
            <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center ${getZIndexClass(Z_INDEX.BOTTOM_SHEET)}`} onClick={handleOverlayClick}>
      <div className="bg-white rounded-t-lg p-4 py-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Give delivery instructions</h2>
        </div>

        <div className="space-y-4">
          {/* Replace image preview/upload with ImageUploader */}
          <ImageUploader
            images={images}
            onChange={handleImagesChange}
            isEditMode={false}
          />

          {/* Text input */}
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            className="w-full min-h-[200px] p-2 rounded-lg border border-gray-200"
            placeholder="write message here..."
            disabled={loading}
          />
        </div>

        <div className="flex justify-between gap-2 mt-4">
          <button
            onClick={handleSave}
            className={`p-3 rounded-lg border-none w-full flex justify-center items-center ${
              (!hasChanges || loading) ? 'bg-gray-400 opacity-50 cursor-not-allowed' : 'bg-amber-300 hover:bg-amber-700'
            }`}
            disabled={!hasChanges || loading}
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
} 